import { HdBodygraphCenter } from '../bodygraph/hd-bodygraph.models';

/**
 * De drie bewustzijnscentra van de Bewustzijn & angsten cheat sheet. Sleutels
 * volgen de `human-design/awareness`- en `human-design/fears`-scopes
 * (`centers.<key>`), niet de bodygraph-center-ids; de mapping daartussen zit in
 * {@link hdFearCenterForBodygraphCenter}.
 */
export type HdFearCenterKey = 'ajna' | 'spleen' | 'solarPlexus';

export interface HdFearCenterDefinition {
  readonly key: HdFearCenterKey;
  /** Bodygraph-center-id zoals de bodygraph en center-info die hanteren. */
  readonly bodygraphCenter: HdBodygraphCenter;
  /** Poortvolgorde zoals op de cheat sheet (en in het rapport-paneel). */
  readonly gates: readonly number[];
  /** Driehoek-diagram van het centrum (gedeeld met de cheat sheet). */
  readonly triangleAsset: string;
}

const HD_FEAR_TRIANGLE_BASE = 'assets/human-design/awareness/';

export const HD_FEAR_CENTERS: readonly HdFearCenterDefinition[] = [
  {
    key: 'ajna',
    bodygraphCenter: 'ajna',
    gates: [47, 24, 4, 11, 43, 17],
    triangleAsset: `${HD_FEAR_TRIANGLE_BASE}ajna-triangle.png`,
  },
  {
    key: 'spleen',
    bodygraphCenter: 'milt',
    gates: [48, 57, 44, 50, 32, 28, 18],
    triangleAsset: `${HD_FEAR_TRIANGLE_BASE}spleen-triangle.png`,
  },
  {
    key: 'solarPlexus',
    bodygraphCenter: 'solarplexus',
    gates: [36, 22, 37, 6, 49, 55, 30],
    triangleAsset: `${HD_FEAR_TRIANGLE_BASE}solar-plexus-triangle.png`,
  },
];

export function getHdFearCenter(key: HdFearCenterKey): HdFearCenterDefinition {
  return HD_FEAR_CENTERS.find(center => center.key === key)!;
}

/** Het bewustzijnscentrum waar deze poort-angst bij hoort, of `null`. */
export function hdFearCenterForGate(gate: number): HdFearCenterDefinition | null {
  return HD_FEAR_CENTERS.find(center => center.gates.includes(gate)) ?? null;
}

/** Vertaalt een bodygraph-center-id naar de fear-center-definitie, of `null`. */
export function hdFearCenterForBodygraphCenter(center: HdBodygraphCenter): HdFearCenterDefinition | null {
  return HD_FEAR_CENTERS.find(definition => definition.bodygraphCenter === center) ?? null;
}

/** Paneel-key voor het per-center angsten-paneel (dedup in de FloatingPanelService). */
export function buildHdCenterFearsPanelKey(center: HdFearCenterKey): string {
  return `center-fears:${center}`;
}

/** `FLOATING_PANEL_DATA` van het per-center angsten-paneel. */
export interface HdCenterFearsPanelData {
  readonly center: HdFearCenterKey;
  /** Poort waar het paneel na openen naartoe scrollt, of `null`. */
  readonly focusGate: number | null;
}
